import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  NotificationsSidebarItem,
  notificationsApiRef,
} from '@backstage/plugin-notifications';
import type { NotificationSignal } from '@backstage/plugin-notifications-common';
import { useSignal } from '@backstage/plugin-signals-react';
import { useEffect } from 'react';
import { useNotificationToasts } from './useNotificationToasts';

// Use the app's shared toast display instead of a separate snackbar stack.
const snackbarProps = { enabled: false };

const requestNativeNotificationPermission = () => {
  if (
    'Notification' in window &&
    window.Notification.permission === 'default'
  ) {
    void window.Notification.requestPermission();
  }
};

export const NativeNotificationsSidebarItem = () => {
  const configApi = useApi(configApiRef);
  const notificationsApi = useApi(notificationsApiRef);
  const { lastSignal } = useSignal<NotificationSignal>('notifications');
  useNotificationToasts(lastSignal);
  const osNotificationsEnabled =
    configApi
      .getOptionalConfigArray('notifications.defaultSettings.channels')
      ?.find(channel => channel.getString('id') === 'Web')
      ?.getOptionalBoolean('enableOSNotification') ?? false;

  useEffect(() => {
    if (
      !osNotificationsEnabled ||
      lastSignal?.action !== 'new_notification' ||
      !('Notification' in window) ||
      window.Notification.permission !== 'granted'
    ) {
      return;
    }

    const notificationId = lastSignal.notification_id;

    notificationsApi
      .getNotification(notificationId)
      .then(notification => {
        const nativeNotification = new window.Notification(
          notification.payload.title,
          {
            body: notification.payload.description,
          },
        );

        nativeNotification.onclick = event => {
          event.preventDefault();
          window.open(
            notification.payload.link ?? '/notifications',
            '_blank',
            'noopener,noreferrer',
          );
          void notificationsApi.updateNotifications({
            ids: [notification.id],
            read: true,
          });
          nativeNotification.close();
        };
      })
      .catch(() => undefined);
  }, [lastSignal, notificationsApi, osNotificationsEnabled]);

  return (
    <div
      onClickCapture={
        osNotificationsEnabled ? requestNativeNotificationPermission : undefined
      }
    >
      <NotificationsSidebarItem snackbarProps={snackbarProps} />
    </div>
  );
};
